<template>
  <div class="video-stage">
    <div v-if="ceremonyVideoLoading" class="player-state" aria-live="polite">
      <span class="state-icon loading"></span>
      <h3>Loading ceremony video</h3>
      <p>Preparing the player and ceremony guide…</p>
    </div>

    <div v-else-if="ceremonyVideoError" class="player-state">
      <span class="state-label">Video unavailable</span>
      <h3>Could not load the ceremony video</h3>
      <p>{{ ceremonyVideoError }}</p>
    </div>

    <video-player
      v-else-if="activeVideoUrl"
      ref="videoPlayer"
      :key="activeVideoUrl"
      class="ceremony-video-player"
      :options="videoOptions"
      @loadeddata="$emit('loadeddata')"
    ></video-player>

    <div v-else class="player-state">
      <span class="state-label">{{ emptyCopy.label }}</span>
      <h3>{{ emptyCopy.title }}</h3>
      <p>{{ emptyCopy.message }}</p>
    </div>
  </div>
</template>

<script>
import '../../../plugins/video-player'

const EMPTY_COPY = {
  idle: {
    label: 'Ceremony player',
    title: 'Select a ceremony',
    message: 'Choose a ceremony above to open its video and guide.',
  },
  not_started: {
    label: 'Not prepared',
    title: 'This ceremony needs preparing',
    message: 'An administrator can start preparation from the tools below.',
  },
  processing: {
    label: 'Preparing',
    title: 'Ceremony Intelligence is being prepared',
    message: 'You can leave this page and return later. Processing continues in the background.',
  },
  failed: {
    label: 'Needs attention',
    title: 'Preparation did not complete',
    message: 'An administrator can review the status and try this ceremony again.',
  },
  no_vectors: {
    label: 'Needs attention',
    title: 'No searchable video was produced',
    message: 'An administrator can review and prepare the ceremony again.',
  },
  unavailable: {
    label: 'Unavailable',
    title: 'Ceremony context could not be loaded',
    message: 'Try again shortly. If the problem continues, ask an administrator to review it.',
  },
}

export default {
  name: 'CeremonyMediaPlayer',
  props: {
    activeVideoUrl: {
      type: String,
      default: '',
    },
    ceremonyVideoLoading: {
      type: Boolean,
      default: false,
    },
    ceremonyVideoError: {
      type: String,
      default: '',
    },
    videoOptions: {
      type: Object,
      required: true,
    },
    readiness: {
      type: String,
      default: 'idle',
    },
  },
  computed: {
    player() {
      return this.$refs.videoPlayer?.player || null
    },
    emptyCopy() {
      return EMPTY_COPY[this.readiness] || EMPTY_COPY.idle
    },
  },
}
</script>

<style scoped>
.video-stage {
  overflow: hidden;
  border-radius: 16px;
  background: #0e1018;
  box-shadow: 0 18px 40px rgba(17, 24, 39, 0.12);
}

.player-state,
.ceremony-video-player {
  width: 100%;
  aspect-ratio: 16 / 9;
}

.player-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 32px;
  background:
    radial-gradient(circle at 20% 10%, rgba(124, 58, 237, 0.24), transparent 34%),
    radial-gradient(circle at 88% 90%, rgba(14, 165, 233, 0.16), transparent 32%),
    #11131d;
  color: #fff;
  text-align: center;
}

.player-state h3 {
  margin: 12px 0 7px;
  font-size: 1.22rem;
  font-weight: 700;
}

.player-state p {
  max-width: 440px;
  margin: 0;
  color: #b8bfcc;
  font-size: 0.82rem;
  line-height: 1.55;
}

.state-label {
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 999px;
  padding: 5px 10px;
  color: #d8dce5;
  font-size: 0.65rem;
  font-weight: 750;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.state-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #a78bfa;
}

.state-icon.loading {
  animation: pulse 1s ease-in-out infinite alternate;
}

.ceremony-video-player {
  position: relative;
  overflow: hidden;
}

.ceremony-video-player :deep(.video-js) {
  width: 100% !important;
  height: 100% !important;
  min-height: 0;
  padding-top: 0 !important;
}

.ceremony-video-player :deep(.video-js video) {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ceremony-video-player :deep(.vjs-big-play-button) {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

@keyframes pulse {
  to {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .state-icon.loading {
    animation: none;
  }
}
</style>
